import { moneyCentsParam as centsParam } from '@/shared/filters/dates';
import { msg, t } from '@lingui/core/macro';

import { defineFilters, type FilterField } from '@/shared/filters/define-filters';
import { defineEntitySource, type EntitySource } from '@/shared/form/entity-source';
import { stripDocumentSeparators } from '@/shared/format/internal';

import {
  CLASSIFICATION,
  CONTRACT_STATUS,
  EMPTY_FILTER_VALUE,
  PAYMENT_DISBURSEMENT_TYPE,
  STATUS_OPERATION,
  STATUS_OPERATION_NOT_FINISHED,
  SUGGESTED_ACTION,
  TREATMENT_FILTER,
  TREATMENT_TYPE_FILTER,
} from './conciliation.enums';

// Filtros das três abas de linhas (components/DataTableFilters/ConciliationDataprevFilters/**). Os
// rótulos são os dos chips do legado (`availableFilters` dos contêineres) e a ordem é a do "Filtrar por".
// CPF, contrato, fundo e corban são texto livre múltiplo com a opção "Vazio" (EmptyableAutocompleteFilter):
// aqui viram entidade múltipla cuja busca devolve o "Vazio", o texto digitado e as opções conhecidas.

export interface FilterOption {
  readonly value: string;
  readonly label: string;
}

const emptyOption = (): FilterOption => ({ value: EMPTY_FILTER_VALUE, label: t`Vazio` });

/** Texto livre com "Vazio" fixo no topo; `known` completa com as opções da conciliação (fundo, corban). */
export function emptyableSource(key: string, known?: (signal: AbortSignal) => Promise<readonly string[]>, scope?: () => unknown): EntitySource<FilterOption> {
  return defineEntitySource<FilterOption>({
    key: ['conciliation', 'filter', key],
    ...(scope ? { scope } : {}),
    search: async (query, signal) => {
      const typed = query.trim();
      let names: readonly string[] = [];
      try {
        names = known ? await known(signal) : [];
      } catch {
        names = [];
      }
      const needle = typed.toLocaleLowerCase();
      const matches = names.filter((name) => needle === '' || name.toLocaleLowerCase().includes(needle)).map((name) => ({ value: name, label: name }));
      const free = typed !== '' && !names.includes(typed) ? [{ value: typed, label: typed }] : [];
      return [emptyOption(), ...free, ...matches];
    },
    byId: (id) => Promise.resolve(id === EMPTY_FILTER_VALUE ? emptyOption() : { value: id, label: id }),
    toOption: (item) => item,
  });
}

/** Valor do campo de moeda (`CurrencyFormField`) em centavos, como o legado (`formatMultiply`). */
export { moneyCentsParam as centsParam } from '@/shared/filters/dates';

/** CPF: só sem separadores (letras do CNPJ alfanumérico ficam); "Vazio" passa como está. */
const documentsParam = (value: string | readonly string[]) =>
  (Array.isArray(value) ? value : [value]).map((item: string) => (item === EMPTY_FILTER_VALUE ? item : stripDocumentSeparators(item)));

export interface LineFilterSources {
  readonly cpfs: EntitySource<FilterOption>;
  readonly contractNumbers: EntitySource<FilterOption>;
  readonly fundNames: EntitySource<FilterOption>;
  readonly tenantDisplayNames: EntitySource<FilterOption>;
}

/** Fontes sem as opções da conciliação: servem ao schema da URL e ao manifesto (a forma é a mesma). */
export const STATIC_SOURCES: LineFilterSources = {
  cpfs: emptyableSource('cpfs'),
  contractNumbers: emptyableSource('contractNumbers'),
  fundNames: emptyableSource('fundNames'),
  tenantDisplayNames: emptyableSource('tenantDisplayNames'),
};

const valueRange = (key: string, label: ReturnType<typeof msg>) => ({ key, type: 'text', label, placeholder: msg`R$ 0,00`, toParam: centsParam }) as const;

/** Campos comuns, por chave, na ordem do "Filtrar por" das Inconsistências (Filters.tsx:42-59). */
function fields(sources: LineFilterSources) {
  return {
    searchString: { key: 'searchString', type: 'search', label: msg`Todos` },
    paymentDisbursementTypes: { key: 'paymentDisbursementTypes', type: 'multiselect', label: msg`Tipo`, options: PAYMENT_DISBURSEMENT_TYPE },
    cpfs: { key: 'cpfs', type: 'entity', multiple: true, label: msg`CPF`, source: sources.cpfs, toParam: documentsParam },
    contractNumbers: { key: 'contractNumbers', type: 'entity', multiple: true, label: msg`Contrato`, source: sources.contractNumbers },
    treatmentFilters: { key: 'treatmentFilters', type: 'multiselect', label: msg`Situação da tratativa`, options: TREATMENT_FILTER },
    fundNames: { key: 'fundNames', type: 'entity', multiple: true, label: msg`Fundo`, source: sources.fundNames },
    tenantDisplayNames: { key: 'tenantDisplayNames', type: 'entity', multiple: true, label: msg`Corban`, source: sources.tenantDisplayNames },
    statusOperation: { key: 'statusOperation', type: 'multiselect', label: msg`Status Operação`, options: STATUS_OPERATION },
    statusOperationNotFinished: { key: 'statusOperation', type: 'multiselect', label: msg`Status Operação`, options: STATUS_OPERATION_NOT_FINISHED },
    contractStatuses: { key: 'contractStatuses', type: 'multiselect', label: msg`Situação do contrato`, options: CONTRACT_STATUS },
    treatmentTypes: { key: 'treatmentTypes', type: 'multiselect', label: msg`Tratativa`, options: TREATMENT_TYPE_FILTER },
    classifications: { key: 'classifications', type: 'multiselect', label: msg`Classificação`, options: CLASSIFICATION },
    suggestedActions: { key: 'suggestedActions', type: 'multiselect', label: msg`Ação sugerida`, options: SUGGESTED_ACTION },
    expectedValueMin: valueRange('expectedValueMin', msg`Valor esperado mín.`),
    expectedValueMax: valueRange('expectedValueMax', msg`Valor esperado máx.`),
    transferredValueMin: valueRange('transferredValueMin', msg`Valor transferido mín.`),
    transferredValueMax: valueRange('transferredValueMax', msg`Valor transferido máx.`),
    suspenseAccountSurplusValueMin: valueRange('suspenseAccountSurplusValueMin', msg`Sobra no Bolsão mín.`),
    suspenseAccountSurplusValueMax: valueRange('suspenseAccountSurplusValueMax', msg`Sobra no Bolsão máx.`),
    suspenseAccountDeductedValueMin: valueRange('suspenseAccountDeductedValueMin', msg`Abatido no Bolsão mín.`),
    suspenseAccountDeductedValueMax: valueRange('suspenseAccountDeductedValueMax', msg`Abatido no Bolsão máx.`),
  } as const satisfies Record<string, FilterField>;
}

const VALUE_KEYS = [
  'expectedValueMin',
  'expectedValueMax',
  'transferredValueMin',
  'transferredValueMax',
  'suspenseAccountSurplusValueMin',
  'suspenseAccountSurplusValueMax',
  'suspenseAccountDeductedValueMin',
  'suspenseAccountDeductedValueMax',
] as const;

/** Inconsistências (ConciliationDataprevInconsistencyFilters.tsx:42-59). */
export function inconsistencyFilters(sources: LineFilterSources = STATIC_SOURCES) {
  const f = fields(sources);
  return defineFilters([
    f.searchString,
    f.paymentDisbursementTypes,
    f.cpfs,
    f.contractNumbers,
    f.treatmentFilters,
    f.fundNames,
    f.tenantDisplayNames,
    f.statusOperation,
    f.contractStatuses,
    f.treatmentTypes,
    f.classifications,
    f.suggestedActions,
    ...VALUE_KEYS.map((key) => f[key]),
  ] as const);
}

/** Não encerradas (ConciliationDataprevNotFinishedFilters.tsx): sem situação da tratativa nem tratativa, status sem "Vazio". */
export function notFinishedFilters(sources: LineFilterSources = STATIC_SOURCES) {
  const f = fields(sources);
  return defineFilters([
    f.searchString,
    f.paymentDisbursementTypes,
    f.cpfs,
    f.contractNumbers,
    f.fundNames,
    f.tenantDisplayNames,
    f.statusOperationNotFinished,
    f.contractStatuses,
    f.classifications,
    f.suggestedActions,
    ...VALUE_KEYS.map((key) => f[key]),
  ] as const);
}

/** Linha de repasse (ConciliationDataprevTransferLineFilters.tsx): sem "Tipo". */
export function transferLineFilters(sources: LineFilterSources = STATIC_SOURCES) {
  const f = fields(sources);
  return defineFilters([
    f.searchString,
    f.cpfs,
    f.contractNumbers,
    f.treatmentFilters,
    f.fundNames,
    f.tenantDisplayNames,
    f.statusOperation,
    f.contractStatuses,
    f.treatmentTypes,
    f.classifications,
    f.suggestedActions,
    ...VALUE_KEYS.map((key) => f[key]),
  ] as const);
}

/** Os conjuntos estáticos: URL (o das Inconsistências é o mais largo) e ações de filtrar do manifesto. */
export const INCONSISTENCY_FILTERS = inconsistencyFilters();
export const NOT_FINISHED_FILTERS = notFinishedFilters();
export const TRANSFER_LINE_FILTERS = transferLineFilters();

/** Filtros em que o "Vazio" é exclusivo (InconsistencyFiltersContainer.tsx:15-23,44-51). */
const EXCLUSIVE_EMPTY = ['classifications', 'suggestedActions', 'cpfs', 'contractNumbers', 'fundNames', 'tenantDisplayNames'] as const;

/**
 * Escolher "Vazio" tira os outros valores do filtro; escolher outro valor tira o "Vazio". O valor novo é o que
 * não estava antes; sem valor novo (remoção), nada muda.
 */
export function exclusiveEmpty(previous: Readonly<Record<string, unknown>>, patch: Record<string, unknown>): Record<string, unknown> {
  const next = { ...patch };
  for (const key of EXCLUSIVE_EMPTY) {
    const value = next[key];
    if (!Array.isArray(value)) continue;
    const before = Array.isArray(previous[key]) ? (previous[key] as unknown[]) : previous[key] === undefined ? [] : [previous[key]];
    const added = (value as string[]).filter((item) => !before.includes(item));
    if (added.length === 0) continue;
    if (added.includes(EMPTY_FILTER_VALUE)) next[key] = [EMPTY_FILTER_VALUE];
    else next[key] = (value as string[]).filter((item) => item !== EMPTY_FILTER_VALUE);
  }
  return next;
}
